import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { api, ApiError, getApiErrorCode } from '../utils/api'
import { clearStoredApiToken, setStoredApiToken, setStoredPrincipal } from '../utils/apiAuth'
import { setActiveTenantKey } from '../offline/tenant'
import { useT } from '../i18n'

export type AuthEntryMode = 'human' | 'token'

export const resolveAuthEntryMode = (pathname: string): AuthEntryMode => pathname === '/token-access'
  ? 'token'
  : 'human'

export const buildAuthEntryHref = ({
  mode,
  redirectTo,
  resetSuccess = false,
  hintedIdentifier = ''
}: {
  mode: AuthEntryMode
  redirectTo: string
  resetSuccess?: boolean
  hintedIdentifier?: string
}): string => {
  const basePath = mode === 'token' ? '/token-access' : '/sign-in'
  const params = new URLSearchParams()
  const nextRedirectTo = redirectTo.trim()
  const nextIdentifier = hintedIdentifier.trim()

  if (nextRedirectTo && nextRedirectTo !== '/home') {
    params.set('to', nextRedirectTo)
  }

  if (mode === 'human' && resetSuccess) {
    params.set('reset', 'success')
  }

  if (mode === 'human' && nextIdentifier) {
    params.set('identifier', nextIdentifier)
  }

  const query = params.toString()
  return query ? `${basePath}?${query}` : basePath
}

export default function TokenSignIn() {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const entryMode = useMemo(() => resolveAuthEntryMode(location.pathname), [location.pathname])
  const isTokenAccessMode = entryMode === 'token'

  const redirectTo = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('to')?.trim() || '/home'
  }, [location.search])
  const resetSuccess = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('reset') === 'success'
  }, [location.search])
  const hintedIdentifier = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('identifier')?.trim() || ''
  }, [location.search])
  const humanAccessHref = useMemo(() => buildAuthEntryHref({
    mode: 'human',
    redirectTo,
    resetSuccess,
    hintedIdentifier
  }), [hintedIdentifier, redirectTo, resetSuccess])
  const tokenAccessHref = useMemo(() => buildAuthEntryHref({
    mode: 'token',
    redirectTo
  }), [redirectTo])

  const [token, setToken] = useState('')
  const [identifier, setIdentifier] = useState(hintedIdentifier)
  const [password, setPassword] = useState('')
  const [tenantKey, setTenantKey] = useState('')
  const [status, setStatus] = useState<'idle' | 'working' | 'error' | 'success'>('idle')
  const [error, setError] = useState<string>('')
  const [resolved, setResolved] = useState<{
    tenantKey: string
    role: string
    actorId: string | null
    tenantMemberId: string | null
    currentTokenId: string | null
    userId: string | null
    tenantMembershipId: string | null
    accountDisplayName: string | null
    email: string | null
    username: string | null
  } | null>(null)

  useEffect(() => {
    if (hintedIdentifier) {
      setIdentifier(hintedIdentifier)
    }
  }, [hintedIdentifier])

  const finishSignIn = (payload: {
    token: string
    principal: {
      tenantKey: string
      role: string
      actorId: string | null
      tenantMemberId: string | null
      currentTokenId: string | null
      userId: string | null
      tenantMembershipId: string | null
      accountDisplayName: string | null
      email: string | null
      username: string | null
    }
  }) => {
    setStoredApiToken(payload.token)
    setStoredPrincipal({
      tenantKey: payload.principal.tenantKey,
      role: payload.principal.role as 'viewer' | 'contributor' | 'editor' | 'admin',
      actorId: payload.principal.actorId,
      tenantMemberId: payload.principal.tenantMemberId,
      currentTokenId: payload.principal.currentTokenId,
      userId: payload.principal.userId,
      tenantMembershipId: payload.principal.tenantMembershipId,
      accountDisplayName: payload.principal.accountDisplayName,
      email: payload.principal.email,
      username: payload.principal.username
    })
    setActiveTenantKey(payload.principal.tenantKey)
    setResolved({
      tenantKey: payload.principal.tenantKey,
      role: payload.principal.role,
      actorId: payload.principal.actorId,
      tenantMemberId: payload.principal.tenantMemberId,
      currentTokenId: payload.principal.currentTokenId,
      userId: payload.principal.userId,
      tenantMembershipId: payload.principal.tenantMembershipId,
      accountDisplayName: payload.principal.accountDisplayName,
      email: payload.principal.email,
      username: payload.principal.username
    })
    setStatus('success')
    navigate(redirectTo, { replace: true })
    globalThis.location.reload()
  }

  const signInWithToken = async () => {
    const value = token.trim()
    if (!value) {
      setError(t('auth.pasteTokenFirst'))
      setStatus('error')
      return
    }

    setStatus('working')
    setError('')
    setResolved(null)

    // Set it first so api client uses it.
    setStoredApiToken(value)

    try {
      const me = await api.me.get()
      finishSignIn({
        token: value,
        principal: {
          tenantKey: me.tenantKey,
          role: me.role,
          actorId: me.actorId,
          tenantMemberId: me.tenantMemberId,
          currentTokenId: me.currentTokenId,
          userId: me.userId,
          tenantMembershipId: me.tenantMembershipId,
          accountDisplayName: me.accountDisplayName,
          email: me.email,
          username: me.username
        }
      })
    } catch (e) {
      clearStoredApiToken()
      setActiveTenantKey(null)
      setStatus('error')
      setError(e instanceof Error ? e.message : t('auth.signInFailed'))
    }
  }

  const signInWithCredentials = async () => {
    const nextIdentifier = identifier.trim()
    if (!nextIdentifier || !password) {
      setError(t('auth.enterCredentialsFirst'))
      setStatus('error')
      return
    }

    setStatus('working')
    setError('')
    setResolved(null)

    try {
      const result = await api.auth.signIn({
        identifier: nextIdentifier,
        password,
        tenantKey: tenantKey.trim() || undefined
      })
      finishSignIn({
        token: result.token,
        principal: {
          tenantKey: result.principal.tenantKey,
          role: result.principal.role,
          actorId: result.principal.actorId,
          tenantMemberId: result.principal.tenantMemberId,
          currentTokenId: result.principal.currentTokenId,
          userId: result.principal.userId,
          tenantMembershipId: result.principal.tenantMembershipId,
          accountDisplayName: result.principal.accountDisplayName,
          email: result.principal.email,
          username: result.principal.username
        }
      })
    } catch (e) {
      clearStoredApiToken()
      setActiveTenantKey(null)
      setStatus('error')
      if (e instanceof ApiError && e.status === 429) {
        setError(t('auth.tooManyAttempts'))
        return
      }
      if (getApiErrorCode(e) === 'memberRevoked') {
        setError(t('auth.memberRevoked'))
        return
      }
      setError(e instanceof Error ? e.message : t('auth.signInFailed'))
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ marginTop: 0 }}>{isTokenAccessMode ? t('auth.titleToken') : t('auth.titleHuman')}</h2>
      <p style={{ color: '#6b7280', marginTop: 8 }}>
        {isTokenAccessMode ? t('auth.helpToken') : t('auth.helpHuman')}
      </p>
      <div style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>
        {isTokenAccessMode ? t('auth.tokenModeNotice') : t('auth.humanModeNotice')}
      </div>
      {resetSuccess && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#ecfdf5', border: '1px solid #bbf7d0', color: '#065f46' }}>
          {t('auth.resetSuccess')}
        </div>
      )}

      <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
        {!isTokenAccessMode && (
          <>
            <label style={{ display: 'grid', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{t('auth.identifier')}</span>
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t('auth.identifierPlaceholder')}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #d1d5db'
                }}
              />
            </label>

            <label style={{ display: 'grid', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{t('auth.password')}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #d1d5db'
                }}
              />
            </label>

            <label style={{ display: 'grid', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{t('auth.tenantKeyOptional')}</span>
              <input
                value={tenantKey}
                onChange={(e) => setTenantKey(e.target.value)}
                placeholder={t('auth.tenantKeyPlaceholder')}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #d1d5db'
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => void signInWithCredentials()}
                disabled={status === 'working'}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  fontWeight: 700,
                  cursor: status === 'working' ? 'not-allowed' : 'pointer'
                }}
              >
                {status === 'working' ? t('auth.signingIn') : t('auth.signIn')}
              </button>

              <button
                type="button"
                onClick={() => {
                  clearStoredApiToken()
                  setActiveTenantKey(null)
                  setToken('')
                  setResolved(null)
                  setStatus('idle')
                  setError('')
                }}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {t('auth.clear')}
              </button>
            </div>

            <div style={{ marginTop: 8, padding: 12, borderRadius: 8, border: '1px solid #d1d5db', backgroundColor: '#f8fafc' }}>
              <div style={{ fontWeight: 700 }}>{t('auth.tokenAccessTitle')}</div>
              <p style={{ margin: '8px 0 0', color: '#475569' }}>{t('auth.tokenAccessHelp')}</p>
              <Link
                to={tokenAccessHref}
                style={{
                  display: 'inline-flex',
                  marginTop: 12,
                  color: '#1d4ed8',
                  fontWeight: 700,
                  textDecoration: 'none'
                }}
              >
                {t('auth.openTokenAccess')}
              </Link>
            </div>
          </>
        )}

        {isTokenAccessMode && (
          <>
            <label style={{ display: 'grid', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{t('auth.apiToken')}</span>
              <textarea
                value={token}
                onChange={(e) => setToken(e.target.value)}
                rows={3}
                placeholder={t('auth.tokenPlaceholder')}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontFamily: 'monospace'
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void signInWithToken()}
                disabled={status === 'working'}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  fontWeight: 700,
                  cursor: status === 'working' ? 'not-allowed' : 'pointer'
                }}
              >
                {status === 'working' ? t('auth.signingIn') : t('auth.signInWithToken')}
              </button>

              <button
                type="button"
                onClick={() => {
                  clearStoredApiToken()
                  setActiveTenantKey(null)
                  setToken('')
                  setResolved(null)
                  setStatus('idle')
                  setError('')
                }}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {t('auth.clear')}
              </button>

              <Link
                to={humanAccessHref}
                style={{
                  color: '#1d4ed8',
                  fontWeight: 700,
                  textDecoration: 'none'
                }}
              >
                {t('auth.backToHumanSignIn')}
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
            {error}
          </div>
        )}

        {status === 'success' && resolved && (
          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#ecfdf5', border: '1px solid #bbf7d0', color: '#065f46' }}>
            {t('auth.signedInAsPrefix')} <b>{resolved.accountDisplayName ?? resolved.username ?? resolved.email ?? resolved.role}</b> {t('auth.onTenant')} <b>{resolved.tenantKey}</b>.
          </div>
        )}
      </div>
    </div>
  )
}
